import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { type LoginPageProps, type SignInMethodType } from './types';
import { CrowdicityLoginForm } from './CrowdicityLoginForm';
import { SoloLoginForm } from './SoloLoginForm';
import { AzureLoginPanel } from './AzureLoginPanel';
import { SamlLoginPanel } from './SamlLoginPanel';
import { OidcLoginPanel } from './OidcLoginPanel';
import { AdmSamlLogin } from './AdmSamlLogin';
import * as Styled from './LoginPage.styled';

const SIGN_IN_ICONS: Record<SignInMethodType, string> = {
  crowdicity: '#crowdworks',
  azure: '#team',
  samlv2: '#team',
  openidsso: '#team'
};

const METHOD_TO_FORM_ID: Record<SignInMethodType, string> = {
  crowdicity: 'cwLogin',
  azure: 'azureLogin',
  samlv2: 'organisationLogin',
  openidsso: 'OpenIDSSOlogin'
};

const METHOD_TO_TAB_ID: Record<SignInMethodType, string> = {
  crowdicity: 'ccOpt',
  azure: 'azureOpt',
  samlv2: 'organisationOpt',
  openidsso: 'OpenIDSSOOpt'
};

export const LoginPage = (props: LoginPageProps) => {
  const {
    communityName,
    hasLogo,
    logoUrl,
    allowCrowdicityLogin,
    allowAzureLogin,
    allowSaml2Login,
    isOidc,
    hasSsoEnabled,
    signInOrderList,
    firstSignInOption,
    crowdicityLoginDisplayName,
    azureDisplayName,
    samlDisplayName,
    oidcProviderName,
    azureDescription,
    samlDescription,
    azureClientId,
    azureRedirectUri,
    sessionTimeoutMinutes,
    authFailError,
    noAccessReasonSSOError,
    returnTo,
    admSamlLoginCommunityUrl,
    isAdm,
    displayRegister,
    isClosed,
    pendingKey,
    isLoggedIn,
    loggedInRedirectUrl,
    postedEmail,
    wasFormPosted,
    base,
    device
  } = props;

  const intl = useIntl();

  const [activeTab, setActiveTab] = useState<string>(firstSignInOption || 'crowdicity');

  useEffect(() => {
    if (isLoggedIn && loggedInRedirectUrl) {
      try {
        window.location.href = atob(loggedInRedirectUrl);
      } catch {
        window.location.href = '/';
      }
    }
  }, [isLoggedIn, loggedInRedirectUrl]);

  const getMethodDisplayName = useCallback(
    (method: SignInMethodType): string => {
      switch (method) {
        case 'crowdicity':
          return (
            crowdicityLoginDisplayName ||
            intl.formatMessage({ id: 'medalliIdeasAccount', defaultMessage: 'Medallia Ideas account' })
          );
        case 'azure':
          return azureDisplayName;
        case 'samlv2':
          return samlDisplayName;
        case 'openidsso':
          return oidcProviderName ? `${oidcProviderName} account` : '';
        default:
          return '';
      }
    },
    [crowdicityLoginDisplayName, azureDisplayName, samlDisplayName, oidcProviderName, intl]
  );

  const isMethodEnabled = useCallback(
    (method: SignInMethodType): boolean => {
      switch (method) {
        case 'crowdicity':
          return allowCrowdicityLogin;
        case 'azure':
          return allowAzureLogin;
        case 'samlv2':
          return allowSaml2Login;
        case 'openidsso':
          return isOidc;
        default:
          return false;
      }
    },
    [allowCrowdicityLogin, allowAzureLogin, allowSaml2Login, isOidc]
  );

  const shouldShowRegister = activeTab === 'crowdicity';

  const handleTabSelect = useCallback((method: string) => {
    setActiveTab(method);
  }, []);

  const handleMobileDropdownChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const formId = e.target.value;
    const method = Object.entries(METHOD_TO_FORM_ID).find(([, id]) => id === formId);
    if (method) {
      setActiveTab(method[0]);
    }
  }, []);

  const activeFormId = METHOD_TO_FORM_ID[activeTab as SignInMethodType] || 'cwLogin';
  const enabledMethods = signInOrderList.filter((method) => isMethodEnabled(method));

  const renderSoloLogin = () => (
    <Styled.SoloLoginContainer>
      {admSamlLoginCommunityUrl ? (
        <>
          <Styled.SoloHeading>
            {intl.formatMessage({ id: 'pleaseSignIn', defaultMessage: 'Please sign in' })}
          </Styled.SoloHeading>
          <Styled.PanelLink href={`https://${admSamlLoginCommunityUrl}/samlc/login?returnTo=${returnTo ?? ''}`}>
            {intl.formatMessage({ id: 'signIn', defaultMessage: 'Sign in' })}
          </Styled.PanelLink>
        </>
      ) : (
        <SoloLoginForm
          base={base}
          returnTo={returnTo}
          sessionTimeoutMinutes={sessionTimeoutMinutes}
          authFailError={authFailError}
          wasFormPosted={wasFormPosted}
          postedEmail={postedEmail}
          isAdm={isAdm}
        />
      )}
    </Styled.SoloLoginContainer>
  );

  const renderMultipleLogin = () => (
    <>
      {admSamlLoginCommunityUrl ? (
        <AdmSamlLogin
          allowSaml2Login={allowSaml2Login}
          samlDescription={samlDescription}
          returnTo={returnTo}
          signInOrderList={signInOrderList}
        />
      ) : (
        <>
          {noAccessReasonSSOError && <Styled.SsoErrorAlert>{noAccessReasonSSOError}</Styled.SsoErrorAlert>}
          <Styled.AllLoginTypeLayout>
            {isClosed && (device === 'android' || device === 'ios') && (
              <Styled.SwitchCommunityContainer
                id={device === 'android' ? 'switch-communities-native-android' : 'switch-communities-native-ios'}
              >
                <Styled.SwitchCommunityButton
                  id={
                    device === 'android'
                      ? 'switch-communities-native-android-login'
                      : 'switch-communities-native-ios-login'
                  }
                  onClick={() => {
                    if (device === 'android' && (window as unknown as Record<string, unknown>).Android) {
                      (
                        (window as unknown as Record<string, unknown>).Android as { switchCommunities: () => void }
                      ).switchCommunities();
                    }
                  }}
                >
                  <span>{intl.formatMessage({ id: 'switchCommunity', defaultMessage: 'Switch Community' })}</span>
                </Styled.SwitchCommunityButton>
              </Styled.SwitchCommunityContainer>
            )}

            <div>
              <Styled.PanelTitle>
                {intl.formatMessage({ id: 'howDoYouWantToSignIn', defaultMessage: 'How do you want to sign in?' })}
              </Styled.PanelTitle>
              <Styled.TabSelector id="signInSelector">
                {enabledMethods.length > 0 ? (
                  enabledMethods.map((method) => (
                    <Styled.TabItem
                      key={method}
                      $active={activeTab === method}
                      id={METHOD_TO_TAB_ID[method]}
                      onClick={() => handleTabSelect(method)}
                    >
                      <Styled.TabIcon>
                        <svg>
                          <use href={SIGN_IN_ICONS[method]} />
                        </svg>
                      </Styled.TabIcon>
                      <Styled.TabLabel>{getMethodDisplayName(method)}</Styled.TabLabel>
                    </Styled.TabItem>
                  ))
                ) : (
                  <Styled.TabItem $active id="ccOpt" onClick={() => handleTabSelect('crowdicity')}>
                    <Styled.TabIcon>
                      <svg>
                        <use href="#crowdworks" />
                      </svg>
                    </Styled.TabIcon>
                    <Styled.TabLabel>
                      {intl.formatMessage({ id: 'medalliIdeasAccount', defaultMessage: 'Medallia Ideas account' })}
                    </Styled.TabLabel>
                  </Styled.TabItem>
                )}
              </Styled.TabSelector>

              {/* Mobile dropdown selector */}
              <Styled.MobileViewSelector
                id="signin-select"
                name="signin-select"
                value={activeFormId}
                onChange={handleMobileDropdownChange}
              >
                {enabledMethods.length > 0 ? (
                  enabledMethods.map((method) => (
                    <option key={method} value={METHOD_TO_FORM_ID[method]}>
                      {getMethodDisplayName(method)}
                    </option>
                  ))
                ) : (
                  <option value="cwLogin">
                    {intl.formatMessage({ id: 'medalliIdeasAccount', defaultMessage: 'Medallia Ideas account' })}
                  </option>
                )}
              </Styled.MobileViewSelector>
            </div>

            {/* Login forms container — only show active form */}
            <Styled.FormsContainer id="loginFormsContainer">
              {activeTab === 'crowdicity' && allowCrowdicityLogin && (
                <CrowdicityLoginForm
                  base={base}
                  returnTo={returnTo}
                  sessionTimeoutMinutes={sessionTimeoutMinutes}
                  authFailError={authFailError}
                  wasFormPosted={wasFormPosted}
                  postedEmail={postedEmail}
                  isAdm={isAdm}
                />
              )}
              {activeTab === 'azure' && allowAzureLogin && (
                <AzureLoginPanel
                  azureClientId={azureClientId}
                  azureRedirectUri={azureRedirectUri}
                  azureDescription={azureDescription}
                />
              )}
              {activeTab === 'samlv2' && allowSaml2Login && (
                <SamlLoginPanel returnTo={returnTo} samlDescription={samlDescription} />
              )}
              {activeTab === 'openidsso' && isOidc && (
                <OidcLoginPanel returnTo={returnTo} oidcProviderName={oidcProviderName} />
              )}
            </Styled.FormsContainer>
          </Styled.AllLoginTypeLayout>
        </>
      )}
    </>
  );

  const registerUrl =
    `/user/registerform?returnTo=${returnTo ?? ''}` + (pendingKey ? `&useraccess_pendingkey=${pendingKey}` : '');

  return (
    <Styled.LoginSection id="signinForm">
      <Styled.LoginHeader>
        {hasLogo && (
          <Styled.LoginImage>
            <img src={logoUrl} alt={communityName} />
          </Styled.LoginImage>
        )}
        <Styled.LoginTitle>{communityName || 'Crowdicity'}</Styled.LoginTitle>
      </Styled.LoginHeader>

      {hasSsoEnabled ? renderMultipleLogin() : renderSoloLogin()}

      {/* Registration footer */}
      {displayRegister && !admSamlLoginCommunityUrl && (
        <Styled.RegistrationFooter
          id="registrationFooter"
          style={{ display: shouldShowRegister || !hasSsoEnabled ? undefined : 'none' }}
        >
          <Styled.RegistrationText>
            {isClosed
              ? intl.formatMessage({
                  id: 'registerClosedCommunity',
                  defaultMessage: 'Please register an account to request access to this community'
                })
              : intl.formatMessage({
                  id: 'registerOpenCommunity',
                  defaultMessage: 'Register an account to join this community?'
                })}
          </Styled.RegistrationText>
          <Styled.RegisterButton href={registerUrl}>
            {intl.formatMessage({ id: 'register', defaultMessage: 'Register' })}
          </Styled.RegisterButton>
        </Styled.RegistrationFooter>
      )}
    </Styled.LoginSection>
  );
};
